import React from "react";
import { useTranslation } from "react-i18next";
import PackageCategory from "../../../../html/js/description/PackageCategory";
import sellTagService from "../../../../js/common/SellTagService";
import { SellTag } from "../../../../js/models/maxEnums";
import currencyFormatter, { Currency } from "../../../../js/common/CurrencyFormatter";
import { ProductDetailInfo } from "../ProductDetailPageTypeConfig";
import productPriceComparisonModalService from "../ProductPriceComparisonModalService";
import ProductGalleryPriceInfo from "./ProductGalleryPriceInfo";

interface ProductGalleryDescriptionContentProperties {
    readonly product: ProductDetailInfo;
    readonly cheapestPrice: number;
    readonly actionDiscountCode: string | null;
    readonly megaClearanceSaleDiscountCode: string | null;
    readonly newProductsDiscountCode: string;
    readonly currency: Currency;

    readonly onAddToBasket: (productId: number) => void;
}

const hasDiscountedPrice = (
    product: ProductDetailInfo,
    actionDiscountCode: string | null,
    megaClearanceSaleDiscountCode: string | null,
): boolean => product.discountedPrice !== null
  && ((actionDiscountCode !== null && sellTagService.contains(product.sellTags, SellTag.Promo))
    || (megaClearanceSaleDiscountCode !== null && sellTagService.contains(product.sellTags, SellTag.MegaClearanceSale)));

export default function ProductGalleryDescriptionContent({
    product,
    cheapestPrice,
    actionDiscountCode,
    megaClearanceSaleDiscountCode,
    newProductsDiscountCode,
    currency,
    onAddToBasket,
}: ProductGalleryDescriptionContentProperties): React.JSX.Element {
    const { t } = useTranslation("detail");

    const handleShowPriceComparisonModal = (): void => {
        productPriceComparisonModalService.showProductPriceComparisonModal(
            newProductsDiscountCode,
            actionDiscountCode,
            megaClearanceSaleDiscountCode,
            cheapestPrice,
            product,
            currency,
            true,
        );
    };

    return (
        <>
            {product.packageCategoryId !== PackageCategory.RepairYourself
              && <p className="fakeh4"><i className="fa-solid fa-info-circle"></i> {t("productIsCheckedPhotoStatusNote")}
              </p>}

            <p dangerouslySetInnerHTML={{ __html: product.statusNote }}></p>

            {hasDiscountedPrice(product, actionDiscountCode, megaClearanceSaleDiscountCode)
              && <p>{t("priceFromKarel")}: <span>{currencyFormatter.formatCurrencyWithSymbol(product.price, currency)}</span>
              </p>}

            <div className="actual-price">

                {product.retailPrice !== null
                  && <p>
                      {t("commonNewProductPrice")}: <span>{currencyFormatter.formatCurrencyWithSymbol(product.retailPrice, currency)}</span>
                      {product.asin !== null
                        && <button
                            type="button" onClick={handleShowPriceComparisonModal}
                            aria-label="Show savings">
                            <i className="fa-solid fa-question-circle"></i>
                        </button>}
                  </p>}

                <p>
                    <ProductGalleryPriceInfo
                        product={product}
                        actionDiscountCode={actionDiscountCode}
                        megaClearanceSaleDiscountCode={megaClearanceSaleDiscountCode}
                        newProductsDiscountCode={newProductsDiscountCode}
                        currency={currency}
                    />
                </p>

                <div className="price-now">
                    {currencyFormatter.formatCurrencyWithSymbol(product.discountedPrice ?? product.price, currency)}
                </div>

                {
                    product.discountValue !== null
                    && <div className="price-discount-tag"> -{product.discountValue} %</div>
                }

                <div className="buttons">
                    <div className="buy-button-container">
                        <button
                            type="button" className="btn gallery-button product-page-buy-button"
                            onClick={(): void => onAddToBasket(product.productId)}>
                            <i className="fa-solid fa-shopping-basket"></i> {t("addToBasket")}
                        </button>
                    </div>
                    <div className="clearfix"></div>
                </div>

            </div>
        </>
    );
}
